import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Archive, RotateCcw, Check, X, EyeOff, Eye, History, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { formatINR } from "@/lib/api";

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return fallback;
};
const NAVY_BTN = "btn-brand text-white";

function ConfirmDelete({ title, description, onConfirm, testId, disabled }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" disabled={disabled} data-testid={testId}
          className="h-9 px-2.5 text-red-600 hover:text-red-700 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function HistoryList({ phoneId, request }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    request("get", `/admin/phones/${phoneId}/price-history`)
      .then((r) => setItems(r.data.history))
      .catch(() => setItems(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneId]);
  if (items === null) return <div className="py-6 text-sm text-slate-500">Loading…</div>;
  if (items === false) {
    return <div data-testid="lcost-price-history-error" className="py-6 text-sm text-red-600">Could not load price history. Please try again.</div>;
  }
  if (items.length === 0) {
    return <div data-testid="lcost-price-history-empty" className="py-6 text-sm text-slate-500">No price changes yet.</div>;
  }
  return (
    <ol data-testid="lcost-price-history-list" className="max-h-80 overflow-y-auto divide-y divide-slate-100">
      {items.map((h) => {
        const diff = h.new_price - h.old_price;
        const pct = h.old_price ? Math.round((diff / h.old_price) * 1000) / 10 : 0;
        return (
          <li key={h.id} data-testid={`lcost-price-history-item-${h.id}`} className="py-3 flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 tabular-nums">
                <span className="text-slate-500 line-through">{formatINR(h.old_price)}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                <span>{formatINR(h.new_price)}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {new Date(h.changed_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </div>
            </div>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-md ${diff > 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {diff > 0 ? "+" : "−"}{formatINR(Math.abs(diff))} ({diff > 0 ? "+" : ""}{pct}%)
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function PriceHistoryDialog({ m, request }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-9 px-2.5" title="Price history"
          data-testid={`lcost-catalog-model-history-${m.id}`}><History className="w-4 h-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{m.brand_name} {m.model_name}</DialogTitle>
          <DialogDescription>
            Current base price <span className="font-semibold text-[#1e429f] bg-[#def7ec] px-1.5 rounded">{formatINR(m.base_price)}</span>
            {m.updated_at && <> · last updated {new Date(m.updated_at).toLocaleDateString("en-IN", { dateStyle: "medium" })}</>}
          </DialogDescription>
        </DialogHeader>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-2">Price history</div>
        <HistoryList phoneId={m.id} request={request} />
      </DialogContent>
    </Dialog>
  );
}

function ModelRow({ m, request, onChanged }) {
  const [name, setName] = useState(m.model_name);
  const [price, setPrice] = useState(m.base_price);
  const dirty = name.trim() !== m.model_name || Number(price) !== Number(m.base_price);
  const valid = name.trim().length >= 2 && price !== "" && Number(price) >= 100 && Number(price) <= 200000;

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Something went wrong")); }
  };
  const save = () => run(() => request("patch", `/admin/phones/${m.id}`,
    { model_name: name.trim(), base_price: Number(price) }), "Model saved");
  const toggle = () => run(() => request("patch", `/admin/phones/${m.id}`, { active: !m.active }),
    m.active ? "Model retired" : "Model restored");
  const remove = () => run(() => request("delete", `/admin/phones/${m.id}`), "Model deleted");

  return (
    <div data-testid={`lcost-catalog-model-${m.id}`}
      className={`grid grid-cols-1 sm:grid-cols-[1fr_9rem_auto] gap-2 items-center p-3 rounded-xl border transition-colors ${m.active ? "border-slate-200 bg-white" : "border-dashed border-slate-300 bg-slate-50"}`}>
      <div className="flex items-center gap-2 min-w-0">
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9"
          data-testid={`lcost-catalog-model-name-${m.id}`} />
        {!m.active && <Badge variant="outline" className="shrink-0 text-slate-500" data-testid={`lcost-catalog-model-retired-${m.id}`}>Retired</Badge>}
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">₹</span>
        <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="pl-7 h-9"
          data-testid={`lcost-catalog-model-price-${m.id}`} />
      </div>
      <div className="flex items-center gap-1 justify-end">
        <Button size="sm" disabled={!dirty || !valid} onClick={save}
          className={`h-9 ${NAVY_BTN}`} data-testid={`lcost-catalog-model-save-${m.id}`}>Save</Button>
        <PriceHistoryDialog m={m} request={request} />
        <Button variant="ghost" size="sm" onClick={toggle} className="h-9 px-2.5"
          title={m.active ? "Retire (hide from customers)" : "Restore"}
          data-testid={`lcost-catalog-model-toggle-${m.id}`}>
          {m.active ? <Archive className="w-4 h-4" /> : <RotateCcw className="w-4 h-4" />}
        </Button>
        <ConfirmDelete testId={`lcost-catalog-model-delete-${m.id}`} onConfirm={remove}
          title={`Delete ${m.model_name}?`}
          description="This removes the model permanently. Past orders keep their details. To just hide it from customers, use Retire instead." />
      </div>
    </div>
  );
}

function AddModelForm({ brandId, request, onChanged }) {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const valid = name.trim().length >= 2 && Number(price) >= 100 && Number(price) <= 200000;
  const add = async () => {
    try {
      await request("post", "/admin/phones", { brand_id: brandId, model_name: name.trim(), base_price: Number(price) });
      toast.success("Model added");
      setName(""); setPrice("");
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not add model")); }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_9rem_auto] gap-2 p-3 rounded-xl bg-[#0e9f6e]/15 border border-[#0e9f6e]/40">
      <Input placeholder="New model name" value={name} onChange={(e) => setName(e.target.value)}
        className="h-9 bg-white" data-testid={`lcost-catalog-add-model-name-${brandId}`} />
      <Input type="number" placeholder="Base price ₹" value={price} onChange={(e) => setPrice(e.target.value)}
        className="h-9 bg-white" data-testid={`lcost-catalog-add-model-price-${brandId}`} />
      <Button size="sm" disabled={!valid} onClick={add} className={`h-9 ${NAVY_BTN}`}
        data-testid={`lcost-catalog-add-model-btn-${brandId}`}>
        <Plus className="w-4 h-4 mr-1" /> Add model
      </Button>
    </div>
  );
}

function BrandHeader({ b, request, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(b.brand_name);
  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); setEditing(false); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Something went wrong")); }
  };
  const activeCount = b.models.filter((m) => m.active).length;
  return (
    <div className="flex flex-wrap items-center gap-2 justify-between mb-3">
      {editing ? (
        <div className="flex items-center gap-1.5">
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-56"
            data-testid={`lcost-catalog-brand-name-input-${b.brand_id}`} />
          <Button size="sm" className={`h-9 ${NAVY_BTN}`} disabled={name.trim().length < 2}
            onClick={() => run(() => request("patch", `/admin/brands/${b.brand_id}`, { brand_name: name.trim() }), "Brand renamed")}
            data-testid={`lcost-catalog-brand-rename-save-${b.brand_id}`}><Check className="w-4 h-4" /></Button>
          <Button size="sm" variant="ghost" className="h-9" onClick={() => { setEditing(false); setName(b.brand_name); }}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-slate-900 text-lg" data-testid={`lcost-catalog-brand-name-${b.brand_id}`}>{b.brand_name}</span>
          <span className="text-xs text-slate-500">{activeCount} live · {b.models.length - activeCount} retired</span>
          {!b.active && <Badge variant="outline" className="text-slate-500">Hidden</Badge>}
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-9 px-2.5" onClick={() => setEditing(true)} title="Rename brand"
          data-testid={`lcost-catalog-brand-rename-${b.brand_id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" className="h-9 px-2.5" title={b.active ? "Hide brand from customers" : "Show brand"}
          onClick={() => run(() => request("patch", `/admin/brands/${b.brand_id}`, { active: !b.active }), b.active ? "Brand hidden" : "Brand visible")}
          data-testid={`lcost-catalog-brand-toggle-${b.brand_id}`}>
          {b.active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </Button>
        <ConfirmDelete testId={`lcost-catalog-brand-delete-${b.brand_id}`} disabled={b.models.length > 0}
          onConfirm={() => run(() => request("delete", `/admin/brands/${b.brand_id}`), "Brand deleted")}
          title={`Delete ${b.brand_name}?`} description="This brand has no models and will be removed permanently." />
      </div>
    </div>
  );
}

function AddBrandForm({ request, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    try {
      await request("post", "/admin/brands", { brand_name: name.trim() });
      toast.success("Brand added. Now add its models.");
      setName("");
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not add brand")); }
  };
  return (
    <div className="flex gap-2">
      <Input placeholder="New brand name" value={name} onChange={(e) => setName(e.target.value)}
        className="h-10 w-full sm:w-56" data-testid="lcost-catalog-add-brand-input" />
      <Button disabled={name.trim().length < 2} onClick={add} className={`h-10 ${NAVY_BTN}`}
        data-testid="lcost-catalog-add-brand-btn"><Plus className="w-4 h-4 mr-1" /> Add brand</Button>
    </div>
  );
}

export function CatalogManager({ catalog, request, onChanged }) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  return (
    <div data-testid="lcost-catalog-manager" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <Input placeholder="Search brand or model…" value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-10 sm:max-w-sm" data-testid="lcost-admin-prices-search" />
        <AddBrandForm request={request} onChanged={onChanged} />
      </div>
      <div className="divide-y divide-slate-200">
        {catalog.map((b) => {
          const brandHit = !q || b.brand_name.toLowerCase().includes(q);
          const models = brandHit ? b.models : b.models.filter((m) => m.model_name.toLowerCase().includes(q));
          if (!brandHit && models.length === 0) return null;
          return (
            <div key={b.brand_id} className="p-4 sm:p-5" data-testid={`lcost-catalog-brand-${b.brand_id}`}>
              <BrandHeader key={b.brand_name} b={b} request={request} onChanged={onChanged} />
              <div className="space-y-2">
                {models.map((m) => (
                  <ModelRow key={`${m.id}-${m.model_name}-${m.base_price}-${m.active}`} m={m} request={request} onChanged={onChanged} />
                ))}
                <AddModelForm brandId={b.brand_id} request={request} onChanged={onChanged} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
